// src/api/shopService.js
// Shop service — calls real backend API for pack purchases
import api from './api';
import { getUserCollection, saveUserCollection } from '../utils/localStorage';

/**
 * Buy a pack of cards from the server
 * @param {string} packId - Pack identifier (standard, premium, ultimate, elemental)
 * @param {Object} options - Additional options (element for elemental pack)
 * @returns {Promise<Object>} Purchase result with drawn cards
 */
export const buyCards = async (packId, options = {}) => {
  try {
    const response = await api.post('/shop/buy', {
      packId,
      element: options.element,
    });
    return response.data;
  } catch (error) {
    console.error('[SHOP] Error buying cards:', error);

    // Fallback: generate cards locally from API card data
    if (error.response && error.response.status === 0) {
      return fallbackBuyCards(packId, options);
    }

    // For non-network errors, try local fallback
    try {
      return fallbackBuyCards(packId, options);
    } catch (e) {
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'achat');
    }
  }
};

// Local fallback (only used when server is unreachable)
async function fallbackBuyCards(packId, options = {}) {
  const { getAllCards } = await import('./cardService');
  const allCardsResponse = await getAllCards({ limit: 1000 });
  const pool = allCardsResponse.cards || [];

  if (pool.length === 0) {
    throw new Error('Aucune carte disponible');
  }

  const packConfig = {
    // R54 : 4 paliers (COMMON/RARE/EPIC/LEGENDARY) — miroir du serveur
    standard: { count: 5, distribution: { COMMON: 0.70, RARE: 0.215, EPIC: 0.065, LEGENDARY: 0.02 } },
    premium: { count: 5, distribution: { COMMON: 0.35, RARE: 0.41, EPIC: 0.19, LEGENDARY: 0.05 } },
    ultimate: { count: 5, distribution: { RARE: 0.45, EPIC: 0.38, LEGENDARY: 0.17 } },
    basic: { count: 5, distribution: { COMMON: 0.70, RARE: 0.215, EPIC: 0.065, LEGENDARY: 0.02 } },
  };

  const pack = packConfig[packId] || packConfig.standard;
  const drawnCards = [];
  const byRarity = {};
  pool.forEach(c => {
    const r = c.rarity || 'NORMAL';
    if (!byRarity[r]) byRarity[r] = [];
    byRarity[r].push(c);
  });

  for (let i = 0; i < pack.count; i++) {
    const roll = Math.random();
    let cumulative = 0;
    let rarity = 'NORMAL';
    for (const [r, p] of Object.entries(pack.distribution)) {
      cumulative += p;
      if (roll < cumulative) { rarity = r; break; }
    }
    const rarityPool = byRarity[rarity] || pool;
    drawnCards.push(rarityPool[Math.floor(Math.random() * rarityPool.length)]);
  }

  // Save to local collection
  const existing = getUserCollection() || [];
  saveUserCollection([...existing, ...drawnCards.map(c => c.id)]);

  return {
    success: true,
    cards: drawnCards,
    totalCost: 0,
    remainingCoins: 0,
    offline: true,
  };
}

/**
 * Get available packs from server
 */
export const getAvailablePacks = async () => {
  try {
    const response = await api.get('/shop/packs');
    return response.data;
  } catch (error) {
    // Fallback
    return {
      success: true,
      packs: [
        { id: 'standard', name: 'Pack Standard', description: '5 cartes aléatoires', price: 100, cardCount: 5 },
        { id: 'premium', name: 'Pack Premium', description: '5 cartes avec meilleures raretés', price: 250, cardCount: 5 },
        { id: 'ultimate', name: 'Pack Ultime', description: 'Meilleures chances de légendaires', price: 500, cardCount: 5 },
      ],
      userCoins: 1000,
    };
  }
};

/**
 * Get user's coin balance
 */
export const getCoinBalance = async () => {
  try {
    const response = await api.get('/shop/balance');
    return response.data;
  } catch (error) {
    return { success: true, coins: 1000 };
  }
};

const shopService = {
  buyCards,
  getAvailablePacks,
  getCoinBalance,
};

export default shopService;
